import type { SuccessResponse } from 'ehs-react';
import type { at } from '../generated/Beans';

type LoginResponse = at.sense.hooks.login.LoginResponse;

export interface LoginRequestDetails {
  userName: string;
  password: string;
  deviceFingerprint?: string;
  clickedItem?: HTMLElement;
  currentStep?: string;
  uniqueAssertionLookupID?: string;
}
/**
 * The collections of JS functions that are used by the old loginhandler.js code.
 */
export interface EHealthLoginUtil {
  onLoginClicked: (target: EventTarget | null | undefined) => void;
  onNextStepClicked: (target: EventTarget | null | undefined) => void;
  onOtpVerificationSuccess: () => void;
  beforeAuthenticateUserCall: (
    target: EventTarget | null | undefined
  ) => LoginRequestDetails;
  responseHandlerAuthenticateUserSuccess: (
    response: SuccessResponse<LoginResponse[]>,
    clickedItem?: HTMLElement
  ) => void;
  responseHandlerAuthenticateUserFailure: (e: Error) => void;
  saveTwoFactorSelection: (selectedTwoFactorMethod: string) => void;
  responseHandlerOtpSendTwoFactorPasswordSuccess: (
    response: SuccessResponse<LoginResponse[]>
  ) => void;
}

/** the class that is added to old JS elements that should not be managed anymore by the old JS code. */
const classReactManaged = 'react-managed';

/**
 * @return the HTMLSelectElement for the second factor selection, or undefined if it does not exist.
 */
export function getSecondFactorSelect(): HTMLSelectElement | undefined {
  const el = document.getElementById('secondFactorType');
  if (el && el instanceof HTMLSelectElement) {
    return el;
  }
  return undefined;
}

/**
 * @return the HTMLInputElement for the OTP password input field, or undefined if it does not exist.
 */
export function getOtpPasswordInput(): HTMLInputElement | undefined {
  const otpEl = document.getElementById('otpassword');
  if (otpEl && otpEl instanceof HTMLInputElement) {
    return otpEl;
  }
  return undefined;
}

/**
 * @return the HTMLSelectElement for the HPD selection, or undefined if it does not exist.
 */
export function getHpdSelect(): HTMLSelectElement | undefined {
  const hpdSelectEl = document.getElementById('companyHPD');
  if (hpdSelectEl && hpdSelectEl instanceof HTMLSelectElement) {
    return hpdSelectEl;
  }
  return undefined;
}

export function saveHpdSelection(
  selectedHpdEntry: at.sense.hooks.login.hpd.HPDParentComboBean
): void {
  const hpdSelectEl = getHpdSelect();
  if (!hpdSelectEl) {
    console.error('No HPD select element found.');
    return;
  }
  const options = hpdSelectEl.children;
  if (options) {
    for (let i = 0; i < options.length; i++) {
      if (options[i] instanceof HTMLOptionElement) {
        const option = options[i] as HTMLOptionElement;
        option.selected = option.value === selectedHpdEntry.oid;
      }
    }
  }
}

/**
 * Disables the old JavaScript view around the input element by hiding them.
 * @param inputEl the input element
 * @return the HTMLElement to which the new REACT view could be rendered.
 */
export function disableOldJsViewAroundInput(
  inputEl: HTMLElement
): HTMLElement | undefined {
  const inputControlGroupEl = inputEl.parentElement;
  if (!inputControlGroupEl) {
    console.error('No input group element found for input element.');
    return undefined;
  }
  const formElementEl = inputControlGroupEl.parentElement;
  if (!formElementEl) {
    console.error('No form element found for input element.');
    return undefined;
  }
  const progressCard = formElementEl.parentElement;
  if (!progressCard) {
    console.error('No progress card element found for input element.');
    return undefined;
  }

  if (formElementEl.classList.contains(classReactManaged)) {
    // was already handled in the past
    return progressCard;
  }

  formElementEl.classList.add(classReactManaged);

  // disable the confirm button:
  const otpConfirmButton = getOtpConfirmButton(formElementEl);
  if (otpConfirmButton) {
    otpConfirmButton.classList.add(classReactManaged);
  }
  return progressCard;
}

function getOtpConfirmButton(
  formElement: HTMLElement
): HTMLElement | undefined {
  const foundButtonHolder = formElement.querySelector('.nextStep');
  if (foundButtonHolder && foundButtonHolder instanceof HTMLElement) {
    return foundButtonHolder as HTMLElement;
  }
  return undefined;
}
